import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../../../shared/context/AuthContext'
import { clearAllAuthState } from '../../../shared/utils/debugAuth'
import { ForgotPasswordModal } from '../../forgot-password'

const LoginForm: React.FC = () => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showForgotPassword, setShowForgotPassword] = useState(false)
  
  const { login, isLoading, isAuthenticated, user } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  
  // Always redirect to dashboard on login to ensure role-based routing
  // Don't trust any cached "from" location to prevent role confusion
  const redirectTo = '/dashboard'

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      console.log('🔄 User already authenticated, redirecting to dashboard for role-based routing', {
        user: { email: user.email, role: user.role },
        redirectTo
      })
      navigate(redirectTo, { replace: true })
    }
  }, [isAuthenticated, user, navigate, redirectTo])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsSubmitting(true)

    if (!email || !password) {
      setError('Please enter both email and password')
      setIsSubmitting(false)
      return
    }

    try {
      console.log('🔐 Attempting login for:', email)
      
      const result = await login(email, password)
      if (!result.success) {
        setError(result.message)
        console.log('❌ Login failed:', result.message)
      } else {
        console.log('✅ Login successful, redirecting to dashboard for role-based routing')
        
        // Always redirect to dashboard to ensure proper role-based routing
        // Don't use any cached redirect location to prevent role confusion
        navigate(redirectTo, { replace: true })
      }
    } catch (error) {
      console.error('Login error:', error)
      setError('An unexpected error occurred. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    )
  }

  return (
    <div className="vh-100 d-flex align-items-center justify-content-center bg-light position-relative">
      <div className="col-md-6 col-lg-4 col-xl-3">
        <div className="card shadow-lg border-0">
          <div className="card-body p-5">
            <div className="text-center mb-4">
              <h2 className="card-title text-primary">🔐 Partner Portal Login</h2>
              <p className="text-muted">Sign in to your account</p>
            </div>

            {error && (
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="mb-3">
                <label htmlFor="email" className="form-label">
                  Email Address
                </label>
                <input
                  type="email"
                  className="form-control"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div className="mb-3">
                <label htmlFor="password" className="form-label">
                  Password
                </label>
                <input
                  type="password"
                  className="form-control"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  disabled={isSubmitting}
                />
              </div>

              <button
                type="submit"
                className="btn btn-primary w-100"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </button>
            </form>

            {/* Back to Home link - positioned at bottom left of card */}
            <div className="mt-3 d-flex justify-content-between">
              <button
                type="button"
                className="btn btn-link text-decoration-none p-0 text-muted"
                onClick={() => navigate('/')}
              >
                ← Back to Home
              </button>
              <button
                type="button"
                className="btn btn-link text-decoration-none p-0 text-muted"
                onClick={() => setShowForgotPassword(true)}
              >
                Forgot Password? →
              </button>
            </div>

          </div>
        </div>
      </div>

      {/* Forgot Password Modal */}
      <ForgotPasswordModal 
        isOpen={showForgotPassword}
        onClose={() => setShowForgotPassword(false)}
      />
    </div>
  )
}

export default LoginForm